import { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import { uploadFileOrFallback } from '../utils/upload'
import { useResolvedUrl } from '../utils/useResolvedUrl'
import { useI18n } from '../utils/i18n'

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''
const UPLOAD_API = API_ORIGIN ? `${API_ORIGIN.replace(/\/$/, '')}/api/upload` : '/api/upload'

function ResolvedImg({ url, alt, className }) {
  const resolved = useResolvedUrl(url || '')
  if (!resolved) return null
  return <img src={resolved} alt={alt || ''} className={className} />
}

export function ImageDropZone({ value, onChange, label, compact = false }) {
  const { tx } = useI18n()
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef(null)
  const resolvedValue = useResolvedUrl(value || '')
  const labelText = label || tx({ zh: '图片', en: 'Image' })

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      await uploadFile(files[0])
    }
  }

  const handleFileSelect = async (e) => {
    const files = e.target.files
    if (files.length > 0) {
      await uploadFile(files[0])
    }
  }

  const uploadFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      setError(tx({ zh: '请选择图片文件', en: 'Please choose an image file' }))
      return
    }

    setUploading(true)
    setError('')

    try {
      const { url, mode } = await uploadFileOrFallback(file, UPLOAD_API, { allowLocalFallback: true })
      onChange(url)
      if (mode === 'local') {
        setError(tx({ zh: '上传接口暂不可用：图片已保存到本机浏览器（仅本机可用）。', en: 'Upload is unavailable: image saved in this browser (local only).' }))
      }
    } catch (e) {
      setError(e.message)
    }

    setUploading(false)
  }

  const handleClear = () => {
    onChange('')
    setError('')
  }

  // compact 模式：更小的封面图片卡片
  if (compact) {
    return (
      <div className="space-y-1">
        <label className="block text-xs text-slate-500 font-bold">{labelText}</label>
        {value ? (
          <div className="relative group">
            <img
              src={resolvedValue}
              alt={tx({ zh: '封面', en: 'Cover' })}
              className="w-full aspect-square object-cover rounded-2xl border-2 border-slate-100"
              onError={(e) => { e.target.style.display = 'none' }}
            />
            <button
              onClick={handleClear}
              className="absolute top-1 right-1 p-1.5 rounded-full bg-slate-900/70 hover:bg-rose-600 text-white opacity-0 group-hover:opacity-100 transition-colors"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              aspect-square border-2 border-dashed rounded-2xl cursor-pointer
              flex flex-col items-center justify-center transition-all
              ${isDragging ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-slate-50'}
              ${uploading ? 'pointer-events-none opacity-60' : ''}
            `}
          >
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
            {uploading ? (
              <div className="w-4 h-4 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin" />
            ) : (
              <Upload size={20} className="text-slate-400" />
            )}
          </div>
        )}
      </div>
    )
  }

  // 标准模式
  return (
    <div className="space-y-2">
      <label className="block text-xs text-slate-500 font-bold">{labelText}</label>
      
      {value ? (
        <div className="relative group">
          <img
            src={resolvedValue}
            alt={tx({ zh: '预览', en: 'Preview' })}
            className="w-full h-32 object-cover rounded-2xl border-2 border-slate-100"
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
          <button
            onClick={handleClear}
            className="absolute top-2 right-2 p-2 rounded-full bg-slate-900/70 hover:bg-rose-600 text-white opacity-0 group-hover:opacity-100 transition-colors"
          >
            <X size={14} />
          </button>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-2 w-full bg-white border-2 border-slate-100 rounded-2xl px-3 py-2 text-slate-800 text-xs outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            placeholder={tx({ zh: '或直接输入 URL', en: 'Or paste an URL' })}
          />
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
            className={`
            relative h-32 border-2 border-dashed rounded-2xl cursor-pointer
            flex flex-col items-center justify-center gap-2 transition-all
            ${isDragging 
              ? 'border-emerald-300 bg-emerald-50' 
              : 'border-slate-200 hover:border-slate-300 bg-slate-50'
            }
            ${uploading ? 'pointer-events-none opacity-60' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {uploading ? (
            <div className="flex items-center gap-2 text-slate-600">
              <div className="w-5 h-5 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin" />
              <span className="text-sm font-bold">{tx({ zh: '上传中...', en: 'Uploading…' })}</span>
            </div>
          ) : (
            <>
              <div className="p-3 rounded-full bg-white border-2 border-slate-100 shadow-sm">
                {isDragging ? <ImageIcon size={24} className="text-emerald-500" /> : <Upload size={24} className="text-slate-400" />}
              </div>
              <span className="text-xs text-slate-500 font-medium">
                {isDragging ? tx({ zh: '松开上传', en: 'Drop to upload' }) : tx({ zh: '拖拽图片或点击上传', en: 'Drag image or click to upload' })}
              </span>
            </>
          )}
        </div>
      )}

      {/* URL 输入（无图片时显示） */}
      {!value && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border-2 border-slate-100 rounded-2xl px-3 py-2 text-slate-800 text-xs outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
          placeholder={tx({ zh: '或直接输入图片 URL', en: 'Or paste an image URL' })}
        />
      )}

      {error && <p className="text-xs text-rose-700 font-bold">{error}</p>}
    </div>
  )
}

export function MultiImageDropZone({ images = [], onChange, label }) {
  const { tx } = useI18n()
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)
  const labelText = label || tx({ zh: '图片列表', en: 'Images' })

  const handleDrop = async (e) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'))
    if (files.length > 0) {
      await uploadFiles(files)
    }
  }

  const uploadFiles = async (files) => {
    setUploading(true)
    const newUrls = []

    for (const file of files) {
      try {
        const { url } = await uploadFileOrFallback(file, UPLOAD_API, { allowLocalFallback: true })
        if (url) newUrls.push(url)
      } catch (e) {
        console.error('Upload failed:', e)
      }
    }

    if (newUrls.length > 0) {
      onChange([...images, ...newUrls])
    }
    setUploading(false)
  }

  const removeImage = (index) => {
    const newImages = [...images]
    newImages.splice(index, 1)
    onChange(newImages)
  }

  const moveImage = (from, to) => {
    if (to < 0 || to >= images.length) return
    const newImages = [...images]
    const [removed] = newImages.splice(from, 1)
    newImages.splice(to, 0, removed)
    onChange(newImages)
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs text-slate-500 font-bold">{labelText}</label>
      
      {/* 已上传图片列表 */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((url, idx) => (
            <div key={idx} className="relative group w-16 h-16">
              <ResolvedImg
                url={url}
                alt={tx({ zh: '图片 {n}', en: 'Image {n}' }, { n: idx + 1 })}
                className="w-full h-full object-cover rounded-2xl border-2 border-slate-100"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                <button
                  onClick={() => moveImage(idx, idx - 1)}
                  className="p-1 text-white/80 hover:text-white text-xs"
                  disabled={idx === 0}
                >
                  ←
                </button>
                <button
                  onClick={() => removeImage(idx)}
                  className="p-1 text-red-400 hover:text-red-300"
                >
                  <X size={14} />
                </button>
                <button
                  onClick={() => moveImage(idx, idx + 1)}
                  className="p-1 text-white/80 hover:text-white text-xs"
                  disabled={idx === images.length - 1}
                >
                  →
                </button>
              </div>
              <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] text-white/60 bg-black/40 rounded-b-lg">
                {idx + 1}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* 拖拽区域 */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          h-20 border-2 border-dashed rounded-2xl cursor-pointer
          flex items-center justify-center gap-2 transition-all
          ${isDragging ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-slate-50'}
          ${uploading ? 'pointer-events-none opacity-60' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => uploadFiles(Array.from(e.target.files))}
          className="hidden"
        />
        {uploading ? (
          <span className="text-xs text-slate-600 font-bold">{tx({ zh: '上传中...', en: 'Uploading…' })}</span>
        ) : (
          <span className="text-xs text-slate-500 font-medium">
            {isDragging ? tx({ zh: '松开上传', en: 'Drop to upload' }) : tx({ zh: '拖拽或点击添加更多图片', en: 'Drag or click to add more images' })}
          </span>
        )}
      </div>
    </div>
  )
}
